import React, { useCallback, useContext, useMemo, useState } from 'react';

import { ChildrenPropType } from '../../../../prop-types/children';
import { useModal } from '../../../../hooks/use-modal';
import { getSellerDataSuccess, sellerProvidersUpdated } from '../../../../store/seller/seller.actions';
import { useDispatch, useSelector } from 'react-redux';
import Global from '../../../../store/global-selectors';
import { ProviderSettingsModal } from './index';

const LabelProviderSettingsContext = React.createContext();

export const useLabelProviderSettingsContext = () => useContext(LabelProviderSettingsContext);

const ProviderSettingsModalContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const sellerData = useSelector(Global.selectSellerData);
  const { close, isOpen, open } = useModal();
  const [selectedProvider, setSelectedProvider] = useState(null);

  const onEditProvider = useCallback(
    (labelProviderId, sellerLabelProviderId) => {
      setSelectedProvider({ labelProviderId, sellerLabelProviderId });
      open();
    },
    [open],
  );

  const closeModal = useCallback(() => {
    setSelectedProvider(null);
    close();
  }, [close]);

  const onSettingsUpdated = useCallback(
    (provider, updatedData) => {
      const newSellerData = {
        ...sellerData,
        labelProviders: sellerData.labelProviders.map((labelProvider) => {
          const isUpdatedProvider = labelProvider.sellerLabelProviderId === provider.accountId;
          if (!isUpdatedProvider) {
            return labelProvider;
          }
          return {
            ...labelProvider,
            accountNumber: updatedData?.accountNumber || labelProvider.accountNumber,
          };
        }),
      };
      dispatch(getSellerDataSuccess(newSellerData));
      dispatch(sellerProvidersUpdated());
      setSelectedProvider(null);
      close();
    },
    [close, dispatch, sellerData],
  );

  const onUpdateSellerData = useCallback(() => {
    dispatch(sellerProvidersUpdated());
  }, [dispatch]);

  const value = useMemo(() => {
    return {
      onEditProvider,
    };
  }, [onEditProvider]);

  return (
    <LabelProviderSettingsContext.Provider value={value}>
      {children}
      {isOpen && selectedProvider && (
        <ProviderSettingsModal
          onClose={closeModal}
          onConfirmed={onSettingsUpdated}
          onUpdateSellerData={onUpdateSellerData}
          provider={selectedProvider}
        />
      )}
    </LabelProviderSettingsContext.Provider>
  );
};

ProviderSettingsModalContextProvider.propTypes = {
  children: ChildrenPropType.isRequired,
};

export default ProviderSettingsModalContextProvider;
